import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getAllPublishedEvents } from '../services/eventService';
import { Event } from '../services/eventService';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}

interface ChatbotContextType {
  messages: Message[];
  isLoading: boolean;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';

const SYSTEM_PROMPT = `You are Eventory's AI assistant, a friendly concierge for event discovery.
Your goal is to help users find events from the knowledge base provided, and talk about the platform and how it is can be usefull.

**RULES:**
- **DO NOT** mention your data source (e.g., "based on the list", "in the JSON"). Just present the information.
- **ALWAYS** use Markdown for formatting. For event lists, use a bulleted list with event titles in bold.

**EXAMPLE:**
"Of course! Here are a few tech events I found:
*   **Tech Conference 2025**
    This is a great event for tech enthusiasts."`;

export const ChatbotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState<Partial<Event>[]>([]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const fetchedEvents = await getAllPublishedEvents();
        setEvents(fetchedEvents);
      } catch (error) {
        console.error("Failed to fetch events for chatbot:", error);
      }
    };

    fetchEvents();
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!OPENROUTER_API_KEY) {
      const missingKeyMessage: Message = {
        id: uuidv4(),
        content: "The AI assistant is not configured correctly. Please contact support. (Reason: Missing API Key)",
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, missingKeyMessage]);
      return;
    }
    
    setIsLoading(true);

    const userMessage: Message = {
      id: uuidv4(),
      content,
      role: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
      
    try {
      // Prepare messages for API
      const eventsContext = events.length > 0
        ? `KNOWLEDGE BASE - CURRENT EVENTS:\n${JSON.stringify(events, null, 2)}`
        : "KNOWLEDGE BASE - CURRENT EVENTS: No events are currently published.";

      const apiMessages = [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'system', content: eventsContext },
        ...messages.map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
        { role: 'user', content },
      ];

      // Call OpenRouter API
      const response = await fetch(OPENROUTER_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Eventory',
        },
        body: JSON.stringify({
          model: 'meta-llama/llama-3-8b-instruct',
          messages: apiMessages,
          temperature: 0.7,
          max_tokens: 1000,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to parse error response.' }));
        const errorMsg = errorData?.error?.message || `API Error: ${response.status} ${response.statusText}`;
        console.error('OpenRouter API error response:', errorMsg);
        throw new Error(errorMsg);
      }

      const data = await response.json();
      let assistantMessage = '';
      if (
        data &&
        Array.isArray(data.choices) &&
        data.choices.length > 0 &&
        data.choices[0] &&
        data.choices[0].message &&
        typeof data.choices[0].message.content === 'string'
      ) {
        assistantMessage = data.choices[0].message.content;
      } else if (data && data.error && data.error.code === 402) {
        assistantMessage = 'Our AI assistant is temporarily unavailable due to usage limits. Please try again later or contact support if the issue persists.';
      } else if (data.error) {
        console.error('API Error:', data.error);
        assistantMessage = `AI service error: ${data.error.message || data.error}`;
      } else {
        console.error('Unexpected API response:', data);
        assistantMessage = 'Sorry, our assistant is temporarily unavailable. Please try again later.';
        if (window.location.hostname === 'localhost') {
          assistantMessage = 'This is a mock response because the AI service is unavailable in development.';
        }
      }

      // Add assistant message
      const newMessage: Message = {
        id: uuidv4(),
        content: assistantMessage,
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, newMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessageContent = (error instanceof Error) 
        ? error.message 
        : 'An unknown error occurred. Please try again later.';

      const errorMessage: Message = {
        id: uuidv4(),
        content: `Sorry, I encountered an error: ${errorMessageContent}`,
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, events]);

  return (
    <ChatbotContext.Provider value={{ messages, isLoading, sendMessage, clearMessages }}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
}; 